import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Bell } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import {
  Notification,
  createNotificationService,
  getNotificationService,
} from '../services/NotificationService';
import { useUserStore } from '../stores/useUserStore';
import { testAblyInBrowser } from '../utils/testAblyConnection';
import { cn } from '../lib/utils';

interface AblyMessage {
  data: any;
  id?: string;
  timestamp?: number;
}

export const NotificationButton = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [notificationService, setNotificationService] = useState<any>(null);

  // Check if user is a manager
  const isManager = user?.roles?.some(
    (role) => role === 'storeManager' || role === 'orgAdmin'
  );

  // Helper function to create notifications from Ably messages
  const createNotificationFromMessage = (
    message: any,
    type: string
  ): Notification => {
    const baseNotification = {
      id: message.data.id || `${type}-${Date.now()}-${Math.random()}`,
      title: message.data.title || getDefaultTitle(type),
      message:
        message.data.message || message.data.body || getDefaultMessage(type),
      timestamp: new Date(message.data.timestamp || Date.now()),
      read: false,
      type: getNotificationType(message.data, type),
      data: message.data,
    };

    return baseNotification;
  };

  // Helper function to get default title based on notification type
  const getDefaultTitle = (type: string): string => {
    switch (type) {
      case 'inventory':
        return '📦 Stock Alert';
      case 'inventory-critical':
        return '🚨 Critical Stock Alert';
      case 'attendance':
        return '👥 Staff Activity';
      case 'shift':
        return '🔄 Shift Update';
      case 'shift-request':
        return '📋 Shift Request';
      case 'general':
      default:
        return '🔔 Notification';
    }
  };

  // Helper function to get default message based on notification type
  const getDefaultMessage = (type: string): string => {
    switch (type) {
      case 'inventory':
        return 'Inventory stock level alert';
      case 'inventory-critical':
        return 'Critical inventory alert requires attention';
      case 'attendance':
        return 'Staff attendance update';
      case 'shift':
        return 'Shift schedule update';
      case 'shift-request':
        return 'Shift request update';
      case 'general':
      default:
        return 'You have a new notification';
    }
  };

  // Helper function to determine notification type (error, warning, info, success)
  const getNotificationType = (
    data: any,
    type: string
  ): 'error' | 'warning' | 'info' | 'success' => {
    // Check for explicit priority or type in data
    if (data.priority === 'CRITICAL' || data.type === 'error') return 'error';
    if (data.priority === 'HIGH' || data.type === 'warning') return 'warning';
    if (data.type === 'success') return 'success';

    // Default based on notification category
    switch (type) {
      case 'inventory-critical':
        return 'error';
      case 'inventory':
        return 'warning';
      case 'attendance':
        return 'info';
      case 'shift':
      case 'shift-request':
        return 'info';
      case 'general':
      default:
        return 'info';
    }
  };

  // Memoize the mock notifications to prevent infinite re-renders
  const getMockNotifications = useCallback(
    (): Notification[] => [
      {
        id: '1',
        title: translate('notifications.new_order.title'),
        message: translate('notifications.new_order.message'),
        timestamp: new Date(Date.now() - 5 * 60 * 1000),
        read: false,
        type: 'success' as const,
      },
      {
        id: '2',
        title: translate('notifications.low_stock.title'),
        message: translate('notifications.low_stock.message'),
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        read: false,
        type: 'warning' as const,
      },
      {
        id: '3',
        title: translate('notifications.system_update.title'),
        message: translate('notifications.system_update.message'),
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        read: true,
        type: 'info' as const,
      },
    ],
    [translate]
  );

  useEffect(() => {
    console.log('NotificationButton useEffect running...');

    // Initialize Ably notification service
    const config = {
      type: 'ably' as const,
      apiKey: import.meta.env.VITE_POS_DASHBOARD_ABLY_API_KEY,
      channel:
        import.meta.env.VITE_POS_DASHBOARD_ABLY_CHANNEL || 'notifications',
      autoConnect: true,
    };

    console.log('Config:', {
      type: config.type,
      apiKey: config.apiKey ? 'Present' : 'Missing',
      channel: config.channel,
    });
    console.log('Environment variables:', {
      VITE_POS_DASHBOARD_ABLY_API_KEY: import.meta.env
        .VITE_POS_DASHBOARD_ABLY_API_KEY
        ? 'Present'
        : 'Missing',
      VITE_POS_DASHBOARD_ABLY_CHANNEL: import.meta.env
        .VITE_POS_DASHBOARD_ABLY_CHANNEL,
      VITE_POS_DASHBOARD_NOTIFICATION_SERVICE_TYPE: import.meta.env
        .VITE_POS_DASHBOARD_NOTIFICATION_SERVICE_TYPE,
    });

    // Debug: Log the actual API key value (first 10 characters for security)
    console.log(
      'API Key value:',
      import.meta.env.VITE_POS_DASHBOARD_ABLY_API_KEY
        ? import.meta.env.VITE_POS_DASHBOARD_ABLY_API_KEY.substring(0, 10) +
            '...'
        : 'undefined'
    );

    // Debug: Log all environment variables
    console.log(
      'All VITE environment variables:',
      Object.keys(import.meta.env)
        .filter((key) => key.startsWith('VITE_'))
        .reduce((obj, key) => {
          obj[key] = import.meta.env[key];
          return obj;
        }, {} as Record<string, any>)
    );

    if (!config.apiKey) {
      console.warn(
        'VITE_POS_DASHBOARD_ABLY_API_KEY not found, using mock notifications'
      );
      console.warn(
        'To enable real-time notifications, set VITE_POS_DASHBOARD_ABLY_API_KEY in your .env file'
      );
      // Fallback to mock notifications if no API key
      const mockNotifications = getMockNotifications();
      setNotifications(mockNotifications);
      setIsConnected(true);

      // Create a mock service for testing
      const mockService = {
        getConnectionStatus: () => true,
        sendNotificationViaAbly: async (notification: any) => {
          console.log('Mock service: sending notification', notification);
          const mockNotification: Notification = {
            id: Date.now().toString(),
            title: notification.title,
            message: notification.message,
            timestamp: new Date(),
            read: false,
            type: notification.type,
          };
          setNotifications((prev) => [mockNotification, ...prev]);
        },
        subscribe: (callback: any) => () => {},
        disconnect: () => {},
      };
      setNotificationService(mockService);
      return;
    }

    console.log('Creating real notification service...');
    const service = createNotificationService(config);
    setNotificationService(service); // Store service reference

    // Subscribe to notifications
    const unsubscribe = service.subscribe((notification: Notification) => {
      console.log('Received notification:', notification);
      setNotifications((prev) => [notification, ...prev]);
    });

    // Subscribe to all notification channels based on user role
    console.log('🔍 Checking Ably client:', service.ablyClient);
    console.log('🔍 Ably client type:', typeof service.ablyClient);
    console.log(
      '🔍 Ably client connection state:',
      service.ablyClient?.connection?.state
    );
    if (service.ablyClient) {
      console.log('✅ Ably client is available, subscribing to channels...');

      // Add connection error handling
      service.ablyClient.connection.on('failed', (error: Error | unknown) => {
        console.error('❌ Ably connection failed:', error);
      });
      service.ablyClient.connection.on(
        'suspended',
        (error: Error | unknown) => {
          console.error('❌ Ably connection suspended:', error);
        }
      );
      service.ablyClient.connection.on(
        'disconnected',
        (error: Error | unknown) => {
          console.error('❌ Ably connection disconnected:', error);
        }
      );

      try {
        // 1. General notifications channel (for all users)
        const generalChannel = service.ablyClient.channels.get('notifications');
        generalChannel.subscribe('notification', (message: AblyMessage) => {
          console.log('📨 Received notification:', message.data);

          // Determine notification type based on the data
          let notificationType = 'general';
          if (message.data.type === 'inventory_stock_alert') {
            notificationType = 'inventory';
          }

          const notification = createNotificationFromMessage(
            message,
            notificationType
          );
          setNotifications((prev) => [notification, ...prev]);
        });
        
        // Subscribe to all manager notification types
        if (isManager) {
          const managerChannel = service.ablyClient.channels.get('manager-notifications');
          
          // Subscribe to all notification types
          const notificationTypes = [
            'new_order',
            'order_status_changed',
            'low_stock',
            'shift_created',
            'shift_switch_request_created',
            'shift_switch_request_approved',
            'shift_switch_request_rejected',
            'new_cost',
            'customer_feedback',
          ];
          
          notificationTypes.forEach((type) => {
            managerChannel.subscribe(type, (message: AblyMessage) => {
              console.log(`📨 Received ${type} notification:`, message.data);
              const notification = createNotificationFromMessage(message, 'general');
              setNotifications((prev) => [notification, ...prev]);
            });
          });
        }

        // 2. Listen for inventory notifications on the general notifications channel
        console.log(
          '🔗 Listening for inventory notifications on general channel...'
        );
        // The general channel subscription above will handle inventory notifications
        // since they're now sent to the 'notifications' channel with event 'notification'

        // 3. Manager notifications channel (for managers only)
        if (isManager) {
          const managerChannel = service.ablyClient.channels.get(
            'manager-notifications'
          );

          // Staff attendance notifications
          managerChannel.subscribe(
            'staff-attendance',
            (message: AblyMessage) => {
              console.log(
                '📨 Received staff attendance notification:',
                message.data
              );
              const notification = createNotificationFromMessage(
                message,
                'attendance'
              );
              setNotifications((prev) => [notification, ...prev]);
            }
          );

          // Critical inventory alerts
          managerChannel.subscribe(
            'inventory_alert',
            (message: AblyMessage) => {
              console.log(
                '📨 Received critical inventory alert:',
                message.data
              );
              const notification = createNotificationFromMessage(
                message,
                'inventory-critical'
              );
              setNotifications((prev) => [notification, ...prev]);
            }
          );
          
          // Note: Other manager notification types (new_order, order_status_changed, etc.)
          // are subscribed above in the general channel subscription section
        }

        // 4. Shift switching notifications (for all users)
        const shiftChannel = service.ablyClient.channels.get(
          'shift-switching-broadcast'
        );
        shiftChannel.subscribe('shift-switched', (message: AblyMessage) => {
          console.log('📨 Received shift switched notification:', message.data);
          const notification = createNotificationFromMessage(message, 'shift');
          setNotifications((prev) => [notification, ...prev]);
        });

        shiftChannel.subscribe('shift-available', (message: AblyMessage) => {
          console.log(
            '📨 Received shift available notification:',
            message.data
          );
          const notification = createNotificationFromMessage(message, 'shift');
          setNotifications((prev) => [notification, ...prev]);
        });

        // 5. User-specific shift notifications (for all users - staff and managers)
        const userId = localStorage.getItem('userId');
        if (userId) {
          const userChannel = service.ablyClient.channels.get(
            `shift-switching-${userId}`
          );

          userChannel.subscribe('request-accepted', (message: AblyMessage) => {
            console.log('📨 Received shift request accepted:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift-request'
            );
            setNotifications((prev) => [notification, ...prev]);
          });

          userChannel.subscribe('request-rejected', (message: AblyMessage) => {
            console.log('📨 Received shift request rejected:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift-request'
            );
            setNotifications((prev) => [notification, ...prev]);
          });

          userChannel.subscribe('request-cancelled', (message: AblyMessage) => {
            console.log('📨 Received shift request cancelled:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift-request'
            );
            setNotifications((prev) => [notification, ...prev]);
          });

          userChannel.subscribe('new-request', (message: AblyMessage) => {
            console.log('📨 Received new shift request:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift-request'
            );
            setNotifications((prev) => [notification, ...prev]);
          });
          
          // Subscribe to shift assignment notifications
          userChannel.subscribe('shift_assigned', (message: AblyMessage) => {
            console.log('📨 Received shift assigned notification:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift'
            );
            setNotifications((prev) => [notification, ...prev]);
          });
          
          // Subscribe to shift update notifications
          userChannel.subscribe('shift_updated', (message: AblyMessage) => {
            console.log('📨 Received shift updated notification:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift'
            );
            setNotifications((prev) => [notification, ...prev]);
          });
          
          // Subscribe to shift cancellation notifications
          userChannel.subscribe('shift_cancelled', (message: AblyMessage) => {
            console.log('📨 Received shift cancelled notification:', message.data);
            const notification = createNotificationFromMessage(
              message,
              'shift'
            );
            setNotifications((prev) => [notification, ...prev]);
          });
        }
        
        // 6. Staff notifications channel (for all staff - orders, etc.)
        const staffChannel = service.ablyClient.channels.get('staff-notifications');
        
        staffChannel.subscribe('new_order', (message: AblyMessage) => {
          console.log('📨 Received new order notification (staff):', message.data);
          const notification = createNotificationFromMessage(message, 'general');
          setNotifications((prev) => [notification, ...prev]);
        });
        
        staffChannel.subscribe('order_status_changed', (message: AblyMessage) => {
          console.log('📨 Received order status change notification (staff):', message.data);
          const notification = createNotificationFromMessage(message, 'general');
          setNotifications((prev) => [notification, ...prev]);
        });

        console.log(
          '✅ Subscribed to all notification channels based on user role'
        );
      } catch (error) {
        console.error(
          '❌ Failed to subscribe to notification channels:',
          error
        );
      }
    }

    // Update connection status
    const checkConnection = () => {
      const status = service.getConnectionStatus();
      console.log('Connection status check:', status);
      setIsConnected(status);
    };

    // Check connection status periodically
    const connectionInterval = setInterval(checkConnection, 5000);
    checkConnection(); // Initial check

    // Cleanup
    return () => {
      unsubscribe();
      clearInterval(connectionInterval);
      service.disconnect();
    };
  }, [getMockNotifications, isManager]);

  const handleNotificationClick = (notification: Notification) => {
    // Mark notification as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );

    // Navigate to check-in/check-out tab for check-in, check-out, and break actions
    const action = notification.data?.action;
    if (
      action === 'checkin' ||
      action === 'checkout' ||
      action === 'break_start' ||
      action === 'break_end'
    ) {
      // Navigate to staff profiles page with checkin tab using query parameter
      navigate('/staff_profiles?tab=checkin');
    }
  };

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleTestAblyConnection = async () => {
    console.log('🧪 Testing Ably connection...');
    await testAblyInBrowser();
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type?: string, data?: any) => {
    // Special icons for manager notifications
    if (data?.action) {
      switch (data.action) {
        case 'checkin':
          return '🟢';
        case 'checkout':
          return '🔴';
        case 'break_start':
          return '☕';
        case 'break_end':
          return '🔄';
        default:
          return '👤';
      }
    }

    // Regular notification icons
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  const getNotificationColor = (type?: string) => {
    switch (type) {
      case 'success':
        return 'default';
      case 'warning':
        return 'default';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getBorderColor = (action?: string) => {
    switch (action) {
      case 'checkin':
        return 'border-l-green-500';
      case 'checkout':
        return 'border-l-red-500';
      case 'break_start':
        return 'border-l-yellow-500';
      case 'break_end':
        return 'border-l-blue-500';
      default:
        return '';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {/* Connection status indicator */}
          <span
            className={cn(
              'absolute bottom-1 right-1 w-2 h-2 rounded-full border border-background',
              isConnected ? 'bg-green-500' : 'bg-red-500'
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[350px] max-h-[400px]">
        {/* Header */}
        <div className="p-4 border-b space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Notifications</h3>
            <Badge
              variant={isConnected ? 'default' : 'destructive'}
              className="text-xs"
            >
              {isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </div>
          {notifications.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleClearAll}
              >
                Clear all
              </Button>
              {import.meta.env.DEV && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleTestAblyConnection}
                >
                  Test Ably
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notifications list */}
        <div className="max-h-[300px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">
                {isConnected
                  ? "You're all caught up!"
                  : 'Connection issue - check your notification service'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    'flex flex-col items-start gap-2 p-3 cursor-pointer',
                    !notification.read && 'bg-accent',
                    notification.data?.action &&
                      getBorderColor(notification.data.action),
                    notification.data?.action && 'border-l-4'
                  )}
                >
                  <div className="flex items-start gap-2 w-full">
                    <span className="text-xl">
                      {getNotificationIcon(
                        notification.type,
                        notification.data
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      {notification.title && (
                        <p className="font-semibold text-sm">
                          {notification.title}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {notification.message}
                      </p>
                    </div>
                    {!notification.read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                    )}
                  </div>
                  <div className="flex justify-between items-center w-full">
                    <span className="text-xs text-muted-foreground">
                      {new Date(notification.timestamp).toLocaleString()}
                    </span>
                    {notification.type && (
                      <Badge
                        variant={getNotificationColor(notification.type) as any}
                        className="text-xs"
                      >
                        {notification.type}
                      </Badge>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
